import { useCallback, useEffect, useRef, useMemo, useState } from "react";
import { Room } from "livekit-client";
import { useChat } from "@livekit/components-react";
import { Send, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

interface ChatMessage {
  id: string;
  message: string;
  sender: string;
  timestamp: number;
  isLocal: boolean;
}

interface MeetingChatPanelProps {
  room: Room;
  currentUserName: string;
  isOpen: boolean;
  onClose: () => void;
}

export function MeetingChatPanel({
  room,
  currentUserName: _currentUserName,
  isOpen,
  onClose,
}: MeetingChatPanelProps) {
  const [message, setMessage] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Sử dụng useChat hook từ LiveKit
  const { chatMessages, send, isSending } = useChat();

  // Chuyển đổi chatMessages từ LiveKit format sang format của component
  const messages = useMemo<ChatMessage[]>(() => {
    return chatMessages.map((msg) => ({
      id: msg.id || `msg-${msg.timestamp}`,
      message: msg.message,
      sender: msg.from?.name || msg.from?.identity || "Unknown",
      timestamp: msg.timestamp,
      isLocal: msg.from?.identity === room.localParticipant.identity,
    }));
  }, [chatMessages, room.localParticipant.identity]);

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!message.trim() || isSending) return;

      try {
        // Sử dụng send function từ useChat hook
        await send(message.trim());
        setMessage("");
      } catch (error) {
        console.error("Error sending message:", error);
      }
    },
    [message, send, isSending]
  );

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 lg:relative lg:inset-auto flex h-[calc(100vh-52px)] lg:h-[calc(100vh-52px)] flex-col p-2 lg:pl-0 w-full lg:w-[350px] xl:w-[400px] z-50 bg-[#0f0f0f] lg:bg-transparent">
      <div className="bg-[#1e1e1e] rounded-lg w-full h-full flex flex-col gap-2 p-2 lg:p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="size-4 sm:size-5" />
            <h3 className="font-semibold text-sm sm:text-base">Trò chuyện</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Đóng chat"
            className="h-8 w-8 p-0"
          >
            ×
          </Button>
        </div>

        <ScrollArea className="flex-1 px-2 bg-[#0f0f0f] rounded-lg p-2">
          <div className="space-y-3 sm:space-y-4 py-2 sm:py-4 h-full">
            {messages.length === 0 ? (
              <div className="flex h-full items-center justify-center text-xs sm:text-sm text-muted-foreground">
                Chưa có tin nhắn nào
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={cn(
                    "flex flex-col gap-1",
                    msg.isLocal ? "items-end" : "items-start"
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[85%] sm:max-w-[80%] rounded-lg px-2 sm:px-3 py-1.5 sm:py-2",
                      msg.isLocal
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-foreground"
                    )}
                  >
                    {!msg.isLocal && (
                      <div className="text-[10px] sm:text-xs font-medium mb-0.5 sm:mb-1 opacity-80">
                        {msg.sender}
                      </div>
                    )}
                    <div className="text-xs sm:text-sm break-words">{msg.message}</div>
                    <div className="text-[10px] sm:text-xs opacity-60 mt-0.5 sm:mt-1">
                      {new Date(msg.timestamp).toLocaleTimeString("vi-VN", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        <form onSubmit={handleSendMessage}>
          <div className="flex gap-1 sm:gap-2 bg-[#0f0f0f] rounded-lg p-1.5 sm:p-2 items-center">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Nhập tin nhắn..."
              className="min-h-8 max-h-20 resize-none bg-transparent border-none outline-none focus-visible:ring-0 focus-visible:ring-offset-0 text-xs sm:text-sm"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
              aria-label="Nhập tin nhắn"
            />
            <Button
              type="submit"
              size="icon"
              variant="ghost"
              disabled={!message.trim() || isSending}
              aria-label="Gửi tin nhắn"
              className="size-7 sm:size-8 shrink-0"
            >
              <Send className="size-3.5 sm:size-4" />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
